import { t } from '@lingui/macro'
import { useFormikContext } from 'formik'
import { type ComponentType, useCallback, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'

import { DateInput } from '@/@bothrs-ui/web/components/forms/date-input/DateInput'
import { FieldArray } from '@/@bothrs-ui/web/components/forms/field-array/FieldArray'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Label } from '@/@bothrs-ui/web/components/forms/label/Label'
import { LegacyMedicationQuestion } from '@/@bothrs-ui/web/components/forms/medication/LegacyMedicationQuestion'
import { Range } from '@/@bothrs-ui/web/components/forms/range/Range'
import { Select } from '@/@bothrs-ui/web/components/forms/select'
import { FormikTextArea } from '@/@bothrs-ui/web/components/forms/text-area/FormikTextArea'
import { FlexColumn, Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { formatMedicationScheduleText } from '@/modules/app/utils/medication.utils'
import { SELF_ADVANCING_TYPES } from '../../enums/questionnaire.enums'
import { splitText } from '../../utils/split-question-text'
import { MultiSelectCommentQuestion } from '../multi-select-comment-question'
import { MultiSelectQuestion } from '../multi-select-question'
import { QuestionFileUpload } from '../question-file-upload/QuestionFileUpload'
import { QuestionInfoSheet } from '../question-info-sheet/QuestionInfoSheet'
import { AllergyQuestion } from '../question-observation/allergy'
import { MedicationQuestion } from '../question-observation/medication'
import { QuestionObservation } from '../question-observation/QuestionObservation'
import { SelectListItem } from '../select-list-item'
import { StSelectListContainer } from './QuestionnaireQuestion.styled'

import type { QuestionAnswerInput, QuestionType } from '@healthblocks-io/core'
import type { QuestionnaireQuestionWithHealthParameters } from 'index'
import type { LegacyMedicationStatement } from '@/@bothrs-ui/web/components/forms/medication/legacy-medication.helpers'
import type { AllergyProfile, HealthParameter, MedicationScheme } from '@/modules/app/types/HealthParameter.types'

type QuestionnaireQuestionProps = {
  nested?: boolean
  noMargin?: boolean
  data: QuestionnaireQuestionWithHealthParameters

  nextDisabled?: boolean
  isSaving?: boolean

  onNext?: () => void
  onPrevious?: () => void
  /**
   * Manually submit an answer to the question without formik
   * @param answer - The answer to submit
   * @param autoAdvance - Whether to automatically advance to the next question
   */
  onManuallySubmitAnswer: (answer: QuestionAnswerInput & { fileIds: number[] }, autoAdvance: boolean) => void
}

const TEXT_INPUT_TYPES: Set<QuestionType> = new Set(['Text', 'Number', 'Date', 'Time'])

/**
 * QuestionnaireQuestion
 *
 * Low-level renderer for a single question.
 * Displays the appropriate input component based on question.type
 * and delegates all state handling to Formik (via useFormikContext).
 *
 * Responsibilities:
 *  - Render input fields (Text, Select, Date, Array, Observation, File, etc.)
 *  - Bind Formik values using the `name={linkId}`
 *  - Render the "Continue" button (type="submit") unless nested
 *  - Disable the "Continue" button when nextDisabled is true
 *
 * Props:
 * @param {Object} data               — Full question definition (IQuestionnaireQuestion)
 * @param {boolean} nested            — Whether this is a nested question (e.g. inside arrays)
 * @param {boolean} noMargin          — Layout override
 * @param {boolean} nextDisabled      — Whether the submit/next button should be disabled
 * @param {function} onNext           — Callback executed on successful submit
 * @param {function} onPrevious       — Optional callback for going back
 *
 * Important:
 *  - This component does NOT interact directly with the engine.
 *    All state updates happen in the parent <Questionnaire> via Formik submit.
 *
 * Example:
 * <QuestionnaireQuestion data={question} nextDisabled={!isValid} />
 */
const QuestionnaireQuestion = ({
  data,
  nested,
  noMargin,
  nextDisabled,
  isSaving,
  onNext = () => {},
  onManuallySubmitAnswer,
}: QuestionnaireQuestionProps) => {
  const theme = useTheme()

  const { setFieldValue, values } = useFormikContext()

  // Template variables
  // ------------------------------------------------------------------------- /
  const questionId: string = useMemo(() => data.linkId ?? data.id, [data.linkId, data.id])

  const [title, description] = useMemo(() => splitText(data.text), [data.text])

  // Render Methods
  // ------------------------------------------------------------------------- /

  const renderTextInput = useCallback(() => {
    const type = data.type.toLowerCase()

    return (
      <Input
        name={questionId}
        type={type}
        inputMode={type === 'number' ? 'decimal' : 'text'}
        placeholder={data.placeholder}
        required={data.required}
        noMargin={noMargin}
        style={type === 'time' ? { width: '120px' } : undefined}
      />
    )
  }, [questionId, data.type, data.required, data.placeholder, noMargin])

  const renderDateInput = useCallback(() => {
    return <DateInput type={'date'} name={questionId} />
  }, [questionId])

  const renderDisplay = useCallback(() => {
    return null
  }, [data.text])

  const renderTextAreaInput = useCallback(() => {
    return (
      <FormikTextArea
        name={questionId}
        placeholder={data.placeholder}
        required={data.required}
        maxLength={data.maxlength}
      />
    )
  }, [questionId, data.placeholder, data.required, data.maxlength])

  const renderSingleSelect = useCallback(() => {
    return (
      <StSelectListContainer>
        {data.answerOption?.map((option) => (
          <SelectListItem
            type='radio'
            key={`${questionId}_${option.value}`}
            id={`${questionId}_${option.value}`}
            name={questionId}
            value={option.value}
            label={option.label}
          />
        ))}
      </StSelectListContainer>
    )
  }, [data.answerOption, questionId])

  const renderSingleSelectAsInput = useCallback(() => {
    return <Select id={questionId} label={title} placeholder={description} options={data.answerOption ?? []} />
  }, [title, description, data.answerOption, questionId])

  const renderMultiSelect = useCallback(() => {
    return <MultiSelectQuestion data={data} questionId={questionId} />
  }, [data, questionId])

  const renderMultiSelectComment = useCallback(() => {
    return <MultiSelectCommentQuestion data={data} questionId={questionId} />
  }, [data, questionId])

  const renderRating = useCallback(() => {
    const numberOfTicks = data.min && data.max && data.step ? (data.max - data.min) / data.step + 1 : undefined
    return (
      <Range
        name={questionId}
        required={data.required}
        step={data.step}
        min={data.min}
        max={data.max}
        numberOfTicks={numberOfTicks}
        maxLabel={data.maxLabel}
        minLabel={data.minLabel}
        showTickmarks={data.showTickmarks}
        noMargin
      />
    )
  }, [questionId, data.required, data.step, data.min, data.max, data.maxLabel, data.minLabel, data.showTickmarks])

  const renderArray = useCallback(() => {
    return <FieldArray questionId={questionId} data={data} onNext={onNext} />
  }, [questionId, data, onNext])

  const renderLegacyMedication = useCallback(() => {
    const rawValue = (values as Record<string, unknown>)?.[questionId]

    const isValidLegacyData = (value: unknown): value is LegacyMedicationStatement[] => {
      if (!Array.isArray(value)) return false
      return value.every(
        (item): item is LegacyMedicationStatement =>
          typeof item === 'object' && item !== null && 'name' in item && 'frequency' in item && 'intake' in item,
      )
    }

    const existingValue: LegacyMedicationStatement[] | undefined = isValidLegacyData(rawValue) ? rawValue : undefined

    return <LegacyMedicationQuestion questionId={questionId} value={existingValue} onNext={onNext} />
  }, [questionId, values, onNext])

  const renderObservation = useCallback(() => {
    // Answer options for observations
    const answerOptions = [
      {
        value: 'keep',
        label: t({ id: 'observation.keep', message: 'Dit is correct' }),
      },
      {
        value: 'edit',
        label: t({ id: 'observation.edit', message: 'Dit wil ik wijzigen' }),
      },
    ]

    return (
      <FlexColumn gap={theme.UI.Spacing.Space8}>
        <StSelectListContainer>
          {data.healthParameter ? (
            <QuestionObservation
              healthParameter={data.healthParameter as HealthParameter}
              shownInQuestionnaire={true}
            />
          ) : null}

          {answerOptions?.map((option) => (
            <SelectListItem
              type='radio'
              key={`${questionId}_${option.value}`}
              id={`${questionId}_${option.value}`}
              name={questionId}
              value={option.value}
              label={option.label}
            />
          ))}
        </StSelectListContainer>
      </FlexColumn>
    )
  }, [data, theme, questionId])

  const renderStructuredObservationQuestion = useCallback(
    <T extends { schemeStatus: string }>(
      Component: ComponentType<{
        questionId: string
        value: T | undefined
        schemeStatus: T['schemeStatus']
        onSubmitAndAdvance: (value: T) => void
      }>,
      formatText?: (value: T) => string,
    ) => {
      const rawValue = (values as Record<string, unknown>)?.[questionId] as T | undefined

      const observationValue = data.observationValue as T | undefined

      const existingValue: T | undefined = rawValue ?? observationValue

      const schemeStatus = (observationValue?.schemeStatus ?? 'unknown') as T['schemeStatus']

      const handleSubmitAndAdvance = (value: T) => {
        setFieldValue(questionId, value)
        onManuallySubmitAnswer(
          {
            linkId: questionId,
            value,
            text: formatText?.(value) ?? '',
            fileIds: [],
          },
          true,
        )
      }

      return (
        <Component
          questionId={questionId}
          value={existingValue}
          schemeStatus={schemeStatus}
          onSubmitAndAdvance={handleSubmitAndAdvance}
        />
      )
    },
    [questionId, values, data.observationValue, setFieldValue, onManuallySubmitAnswer],
  )

  const renderMedication = useCallback(
    () => renderStructuredObservationQuestion<MedicationScheme>(MedicationQuestion, formatMedicationScheduleText),
    [renderStructuredObservationQuestion],
  )

  const renderAllergy = useCallback(
    () => renderStructuredObservationQuestion<AllergyProfile>(AllergyQuestion),
    [renderStructuredObservationQuestion],
  )

  const renderFileUpload = useCallback(() => {
    /**
     * Link a file to an answer
     *  - upload: Add new uploaded file or keep existing file in answer
     *  - skip: Skip file upload and clear files from answer
     *  (This will sync the answer with Formik state + sync with history)
     * @param fileId - The file id of the uploaded file
     * @param state - The state of the file upload
     */
    const linkFileToAnswer = async (fileId: number | null, state: 'upload' | 'skip') => {
      if (data.linkId) {
        switch (state) {
          case 'upload': {
            const answer: QuestionAnswerInput & { fileIds: number[] } = {
              linkId: data.linkId,
              value: { fileIds: fileId ? [fileId] : [] },
              fileIds: fileId ? [fileId] : [],
            }

            await setFieldValue(data.linkId, answer.value)
            onManuallySubmitAnswer(answer, true)

            break
          }
          case 'skip': {
            // Skip file upload and clear files from answer
            const answer: QuestionAnswerInput & { fileIds: number[] } = {
              linkId: data.linkId,
              value: { fileIds: [] },
              fileIds: [],
            }

            await setFieldValue(data.linkId, answer.value)
            onManuallySubmitAnswer(answer, true)
          }
        }
      }
      // Note: Navigation is handled by QuestionFileUpload via onNext prop
    }

    return (
      <QuestionFileUpload
        data={data}
        answer={data.linkId ? (values as Record<string, QuestionAnswerInput | undefined>)[data.linkId] : undefined}
        onPhotoChange={linkFileToAnswer}
        onNext={onNext}
      />
    )
  }, [data, setFieldValue, onNext])

  const renderInputByType = () => {
    if (TEXT_INPUT_TYPES.has(data.type)) {
      return renderTextInput()
    }

    switch (data.type) {
      case 'Date':
      case 'BirthDate': {
        return renderDateInput()
      }
      case 'LongText': {
        return renderTextAreaInput()
      }
      case 'QuickReplies': {
        return renderSingleSelect()
      }
      case 'Custom_QuickReplies': {
        return renderSingleSelectAsInput()
      }
      case 'MultiSelect': {
        return renderMultiSelect()
      }
      case 'MultiSelectComment': {
        return renderMultiSelectComment()
      }
      case 'Rating': {
        return renderRating()
      }
      case 'Display': {
        return renderDisplay()
      }
      case 'Array': {
        return renderArray()
      }
      case 'Observation': {
        return renderObservation()
      }
      case 'File': {
        return renderFileUpload()
      }
      case 'Medication': {
        return questionId === 'medication_scheme' ? renderLegacyMedication() : renderMedication()
      }
      case 'Allergy': {
        return renderAllergy()
      }
      default: {
        return null
      }
    }
  }

  return (
    <StQuestionnaireQuestionContainer>
      <StQuestionContent>
        {(title || description) && !nested ? (
          <StQuestionHeader>
            {title ? (
              <H2>
                {title}
                {data.extraInfo ? (
                  <QuestionInfoSheet title={title} extraInfo={data.extraInfo} questionLinkId={questionId} />
                ) : null}
              </H2>
            ) : null}
            {description ? (
              <P
                type='BodyLargeRegular'
                color={theme.Colors.Neutrals.Darkest}
                style={{ marginBottom: theme.UI.SpacingPx.Space2 }}
              >
                {description}
                {!title && data.extraInfo ? (
                  <QuestionInfoSheet extraInfo={data.extraInfo} questionLinkId={questionId} />
                ) : null}
              </P>
            ) : null}
          </StQuestionHeader>
        ) : null}

        <Padding p={nested ? 0 : theme.UI.Spacing.Space6} pt={nested ? 0 : theme.UI.Spacing.Space3}>
          {nested && title ? <H2>{title}</H2> : null}
          {nested && description ? <Label htmlFor=''>{description}</Label> : null}

          {renderInputByType()}
        </Padding>
      </StQuestionContent>

      {nested || SELF_ADVANCING_TYPES.has(data.type) ? null : (
        <StActionContainer>
          <Button type='button' variant='Primary' onClick={onNext} disabled={nextDisabled} isLoading={isSaving}>
            {t({
              id: 'globals.continue',
              comment: 'Global for continue',
              message: 'Ga verder',
            })}
          </Button>
        </StActionContainer>
      )}
    </StQuestionnaireQuestionContainer>
  )
}

export { QuestionnaireQuestion }

const StQuestionnaireQuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
`

const StQuestionHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  ${({ theme }) => `
    background-color: ${theme.Colors.Background.White};
    padding: ${theme.UI.SpacingPx.Space10} ${theme.UI.SpacingPx.Space6} 0;
  `}

  @media ${breakpoints.s} {
    padding-top: 0;
  }
`

const StQuestionContent = styled.div`
  flex-grow: 1;
`

const StActionContainer = styled.div`
  flex-grow: 0;
  grid-area: button;
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);
  position: sticky;
  bottom: 0;
  z-index: 1;

  ${({ theme }) => `
    background-color: ${theme.Colors.Background.White};
    padding: ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space6};
    padding-bottom: max(var(--safe-area-inset-bottom), 20px);
  `} @media ${breakpoints.s} {
    box-shadow: none;
    padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
  }
`
